import React, { useMemo } from "react";

import {
  faCog,
  faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import { Link } from "react-router-dom";

import {
  computeModelUsageDrift,
  MODEL_USAGE_FIELD_LABELS,
  ModelUsageDrift,
} from "src/adminPanel/modelQuotas/drift";
import { Module } from "src/api/modulesApi";
import { Button } from "src/base-components/Button";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { Pill, PillVariants } from "src/base-components/Pill";
import { Cell, TableComp } from "src/base-components/Table";
import {
  FlowApiSchemasModelUsageTier,
  WorkspaceConfig,
  WorkspaceDataplane,
} from "src/clients/flow-api";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";

export type ModelQuotaRow = {
  workspaceId: string;
  workspaceName: string;
  workspaceSlug: string;
  orgId: string;
  orgName: string;
  tier: FlowApiSchemasModelUsageTier;
  agentsModuleId: string | undefined;
  awsAccountName: string | undefined;
  workspace: WorkspaceDataplane;
  drift: ModelUsageDrift;
};

const TIER_PILL_VARIANT: Record<FlowApiSchemasModelUsageTier, PillVariants> = {
  [FlowApiSchemasModelUsageTier.DEFAULT]: "gray",
  [FlowApiSchemasModelUsageTier.PREMIUM]: "indigo-light",
  [FlowApiSchemasModelUsageTier.CUSTOM]: "orange",
};

const TIER_LABEL: Record<FlowApiSchemasModelUsageTier, string> = {
  [FlowApiSchemasModelUsageTier.DEFAULT]: "Default",
  [FlowApiSchemasModelUsageTier.PREMIUM]: "Premium",
  [FlowApiSchemasModelUsageTier.CUSTOM]: "Custom",
};

export const buildModelQuotaRows = (
  workspaces: WorkspaceDataplane[],
  orgMap: Map<string, string>,
  agentsModuleByWorkspaceId: Map<string, Module>,
  awsAccountNameByUuid: Map<string, string>,
): ModelQuotaRow[] =>
  workspaces.map((ws) => {
    const config = (ws.config ?? {}) as WorkspaceConfig;
    const agentsModule = agentsModuleByWorkspaceId.get(ws.id);
    return {
      workspaceId: ws.id,
      workspaceName: ws.name,
      workspaceSlug: ws.slug,
      orgId: ws.organization_id,
      orgName: orgMap.get(ws.organization_id) ?? "",
      tier: config.model_usage_tier ?? FlowApiSchemasModelUsageTier.DEFAULT,
      agentsModuleId: agentsModule?.id,
      awsAccountName: ws.aws_account_id
        ? awsAccountNameByUuid.get(ws.aws_account_id)
        : undefined,
      workspace: ws,
      drift: computeModelUsageDrift(config, agentsModule),
    };
  });

const columnHelper = createColumnHelper<ModelQuotaRow>();

type Props = {
  rows: ModelQuotaRow[];
  onEdit: (row: ModelQuotaRow) => void;
};

export const ModelQuotasTable: React.FC<Props> = ({ rows, onEdit }) => {
  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.orgName || row.orgId, {
        id: "organization",
        header: () => (
          <div className="px-4 py-3 font-inter-medium-12px text-gray-800">
            Organization
          </div>
        ),
        cell: (info) => {
          const row = info.row.original;
          return (
            <Cell className="px-4" info={info}>
              <span className="inline-flex items-center gap-1">
                <Link
                  className="text-blue-600 transition-colors hover:text-blue-800 hover:underline"
                  to={`/admin-panel/organizations?search=${row.orgId}`}
                >
                  <span className="font-inter-normal-13px">
                    {row.orgName || row.orgId.slice(0, 8)}
                  </span>
                </Link>
                <CopyTextIcon
                  feedback="inline"
                  size="xs"
                  tooltip="Copy organization ID"
                  value={row.orgId}
                />
              </span>
            </Cell>
          );
        },
        enableSorting: true,
      }),
      columnHelper.accessor((row) => row.workspaceName, {
        id: "workspace",
        header: () => (
          <div className="px-4 py-3 font-inter-medium-12px text-gray-800">
            Workspace
          </div>
        ),
        cell: (info) => {
          const row = info.row.original;
          return (
            <Cell className="px-4" info={info}>
              <div className="flex items-center gap-1">
                <div className="flex flex-col">
                  <Link
                    className="text-blue-600 transition-colors hover:text-blue-800 hover:underline"
                    to={`/admin-panel/organizations?search=${row.workspaceId}`}
                  >
                    <span className="font-inter-normal-13px">
                      {row.workspaceName}
                    </span>
                  </Link>
                  <span className="font-inter-normal-12px text-gray-500">
                    {row.workspaceSlug}
                  </span>
                </div>
                <CopyTextIcon
                  feedback="inline"
                  size="xs"
                  tooltip="Copy workspace ID"
                  value={row.workspaceId}
                />
              </div>
            </Cell>
          );
        },
        enableSorting: true,
      }),
      columnHelper.accessor((row) => row.tier, {
        id: "tier",
        header: () => (
          <div className="px-4 py-3 font-inter-medium-12px text-gray-800">
            Model tier
          </div>
        ),
        cell: (info) => {
          const tier = info.getValue();
          return (
            <Cell className="px-4" info={info}>
              <Pill variant={TIER_PILL_VARIANT[tier]}>
                <Pill.Text>{TIER_LABEL[tier]}</Pill.Text>
              </Pill>
            </Cell>
          );
        },
        enableSorting: true,
      }),
      columnHelper.accessor((row) => row.agentsModuleId !== undefined, {
        id: "agentsModule",
        header: () => (
          <div className="px-4 py-3 font-inter-medium-12px text-gray-800">
            Agents module
          </div>
        ),
        cell: (info) => {
          const deployed = info.getValue();
          const drift = info.row.original.drift;
          return (
            <Cell className="px-4" info={info}>
              <div className="flex items-center gap-2">
                <Pill variant={deployed ? "green" : "gray"}>
                  <Pill.Text>
                    {deployed ? "Deployed" : "Not deployed"}
                  </Pill.Text>
                </Pill>
                {drift.hasDrift && (
                  <Tooltip
                    asChild
                    title={`Workspace config differs from agents module on: ${drift.fields
                      .map((f) => MODEL_USAGE_FIELD_LABELS[f])
                      .join(", ")}`}
                  >
                    <span
                      className="inline-flex"
                      data-loc={`drift-warning-${info.row.original.workspaceId}`}
                    >
                      <Icon
                        icon={faTriangleExclamation}
                        size="sm"
                        variant="warning"
                      />
                    </span>
                  </Tooltip>
                )}
              </div>
            </Cell>
          );
        },
        enableSorting: true,
      }),
      columnHelper.display({
        id: "actions",
        header: () => <div className="w-10 px-4 py-3" />,
        cell: (info) => (
          <Cell className="px-4" info={info}>
            <Button
              aria-label="Edit model quotas"
              dataLoc={`edit-quota-${info.row.original.workspaceId}`}
              size="sm"
              variant="secondary"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(info.row.original);
              }}
            >
              <Icon icon={faCog} size="sm" />
            </Button>
          </Cell>
        ),
        meta: { displayName: "Actions", sticky: "right" as const },
      }),
    ],
    [onEdit],
  );

  return (
    <div className="flex max-h-[calc(100vh-340px)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <TableComp
        columns={columns}
        data={rows}
        dataLoc="model-quotas-table"
        disableVirtualization
        getRowId={(row) => row.workspaceId}
        variant="default"
      />
    </div>
  );
};
